import React, { useEffect, useState } from "react";
import ReviewsExampleDesktop from "/app/assets/images/elements/reviews_example_desktop.svg";
import ReviewsExampleMobile from "/app/assets/images/elements/reviews_example_mobile.svg";
import { buildReviewsStylesheet } from "./ElementReviewsStyleResolver";
import BuilderStyleTag from "../shared/BuilderStyleTag";

const renderStars = (rating) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  const stars = [];
  for (let i = 0; i < full; i++) stars.push("full");
  if (half) stars.push("half");
  for (let i = 0; i < empty; i++) stars.push("empty");
  return stars;
};

const ReviewContentBoxPlaceholder = ({ review }) => (
  <div className="cf-reviews-content-box cf-reviews-content-type">
    <div className="cf-reviews-content-wrapper">
      {review.rating != null && (
        <div className="cf-reviews-rating" aria-hidden="true">
          {renderStars(review.rating).map((type, i) => (
            <span key={i} className={`cf-reviews-star cf-reviews-star-${type}`}>
              ★
            </span>
          ))}
        </div>
      )}
      {review.title && <div className="cf-reviews-title cf-reviews-content-type">{review.title}</div>}
      {review.content && <div className="cf-reviews-content">{review.content}</div>}
      <div className="cf-reviews-customer-profile">
        {review.customer_avatar && (
          <div className="cf-reviews-customer-profile-avatar">
            <img src={review.customer_avatar} alt="" />
          </div>
        )}
        <div className="cf-reviews-customer-column-wrapper">
          {review.customer_name && (
            <span className="cf-reviews-customer-profile-name">{review.customer_name}</span>
          )}
          {review.customer_title && (
            <span className="cf-reviews-customer-profile-title">{review.customer_title}</span>
          )}
        </div>
      </div>
      {review.date && <div className="cf-reviews-date">{review.date}</div>}
    </div>
  </div>
);

const ReviewContentBox = React.memo(({ review, element, builder, variantOptions }) => {
  const reviewService = typeof globalThis !== "undefined" ? globalThis.ReviewService : undefined;

  if (!reviewService) {
    return <ReviewContentBoxPlaceholder key={`cf-reviews-content-box-${review.id}`} review={review} />;
  }

  const renderer = new reviewService.ReviewRenderer(review, element, { builder }, variantOptions || {});
  const renderData = renderer.renderData();

  return (
    <div
      className={renderData.containerClasses}
      key={`cf-reviews-content-box-${review.id}`}
      dangerouslySetInnerHTML={{
        __html: renderData.isPositionedVideo
          ? `<div class="cf-reviews-main-content cf-reviews-video-layout-${renderData.videoPositioning}">${renderData.mainContentHtml}</div>`
          : `${renderData.backgroundVideoHtml}<div class="cf-reviews-content-wrapper">${renderData.mainContentHtml}</div>`
      }}
    />
  );
});

const generateSampleReviews = (count, existingReviews = []) => {
  const sampleData = [
    {
      title: "Amazing product! (Sample)",
      content: "This product exceeded my expectations. The quality is outstanding and I would definitely recommend it to others.",
      customer_name: "Sarah Johnson",
      customer_title: "Verified Buyer",
      rating: 5,
      date: new Date().toISOString().split('T')[0],
      customer_avatar: "https://i.pravatar.cc/150?img=1",
      brand_logo: null,
      video: null,
      options: {},
      brand_logo: "https://uploads.convertflow.co/production/websites/65918/gydLUaK7RuX6CInN05ie_image.webp"
    },
    {
      title: "Great value for money (Sample)",
      content: "I've been using this for a few weeks now and I'm really impressed. It's exactly what I was looking for.",
      customer_name: "Michael Chen",
      customer_title: "Happy Customer",
      rating: 4,
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      customer_avatar: "https://i.pravatar.cc/150?img=2",
      brand_logo: null,
      video: null,
      options: {},
      brand_logo: "https://uploads.convertflow.co/production/websites/65918/gydLUaK7RuX6CInN05ie_image.webp"
    },
    {
      title: "Highly recommend! (Sample)",
      content: "Fast shipping, great packaging, and the product itself is fantastic. Will definitely order again.",
      customer_name: "Emily Rodriguez",
      customer_title: "Satisfied Customer",
      rating: 3,
      date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      customer_avatar: "https://i.pravatar.cc/150?img=3",
      brand_logo: null,
      video: null,
      options: {},
      brand_logo: "https://uploads.convertflow.co/production/websites/65918/gydLUaK7RuX6CInN05ie_image.webp"
    },
    {
      title: "Perfect fit (Sample)",
      content: "The quality is excellent and it fits perfectly. Very happy with my purchase and the customer service was great too.",
      customer_name: "David Thompson",
      customer_title: "Verified Buyer",
      rating: 5,
      date: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      customer_avatar: "https://i.pravatar.cc/150?img=4",
      brand_logo: null,
      video: null,
      options: {},
      brand_logo: "https://uploads.convertflow.co/production/websites/65918/gydLUaK7RuX6CInN05ie_image.webp"
    },
    {
      title: "Love it! (Sample)",
      content: "This is exactly what I needed. The design is beautiful and it works perfectly. Couldn't be happier!",
      customer_name: "Jessica Martinez",
      customer_title: "Happy Customer",
      rating: 4,
      date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      customer_avatar: "https://i.pravatar.cc/150?img=5",
      brand_logo: null,
      video: null,
      options: {},
      brand_logo: "https://uploads.convertflow.co/production/websites/65918/gydLUaK7RuX6CInN05ie_image.webp"
    },
    {
      title: "Excellent quality (Sample)",
      content: "I'm very impressed with the quality and attention to detail. This is a great product at a fair price.",
      customer_name: "Robert Williams",
      customer_title: "Satisfied Customer",
      rating: 3,
      date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      customer_avatar: "https://i.pravatar.cc/150?img=6",
      brand_logo: null,
      video: null,
      options: {},
      brand_logo: "https://uploads.convertflow.co/production/websites/65918/gydLUaK7RuX6CInN05ie_image.webp"
    }
  ];

  const sampleReviews = [];
  const existingIds = new Set(existingReviews.map(r => r.id));
  
  for (let i = 0; i < count; i++) {
    const sampleIndex = i % sampleData.length;
    const baseSample = sampleData[sampleIndex];
    
    // Create a unique ID for sample reviews (negative IDs to avoid conflicts)
    const sampleId = `sample-${i}-${Date.now()}`;
    
    // Skip if this sample already exists (shouldn't happen, but safety check)
    if (!existingIds.has(sampleId)) {
      // Use the base sample data, but vary dates to make them feel more realistic
      sampleReviews.push({
        ...baseSample,
        id: sampleId,
        date: new Date(Date.now() - (i + 1) * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
    }
  }

  return sampleReviews;
};

const getInitialReviewsForPreview = (preview, builder) => {
  if (preview !== true && builder !== true) return [];
  return generateSampleReviews(3, []);
};

const ElementReviews = (props) => {
  const element = props.element;
  const shouldRenderVideoPlaceholders =
    props.builder == true || Number(props.website?.id) === 0;
  const variantOptions = (props.variant && props.variant.options) || {};

  const [reviewsList, setReviewsList] = useState(() =>
    getInitialReviewsForPreview(props.preview, props.builder)
  );
  const [currentSlide, setCurrentSlide] = useState(0);

  const reviewsPerRow = parseInt(element.options['reviews-per-row']) || 3;
  const reviewsPerRowMobile = parseInt(element.options['reviews-per-row-mobile']) || 1;

  // Settings
  const sliderEnabled = element.options['slider-enabled'] !== 'false';
  const contentFlow = element.options['slider-enabled'] == 'true' ? 'rows' : element.options['content-flow'];
  const sliderStyle = element.options['slider-style'] || 'default';
  const showIndicators = element.options['slider-show-indicators'] !== 'false';
  const showArrows = element.options['slider-show-arrows'] !== 'false';
  const reviewsLayout = element.options['reviews-layout'] || 'horizontal';

  let sortedReviews = reviewsList;

  const reviewsPerSlide = props.device === "mobile" ?
    reviewsPerRowMobile :
    reviewsPerRow;

  const stylesheet = buildReviewsStylesheet(
    props.builder,
    props.device,
    props.cta,
    element,
    props.variant
  );

  const [reviewsColumns, setReviewsColumns] = useState({});

  useEffect(() => {
    const integrationType = element.options["reviews-source"] || "ConvertFlow";
    const integrationObject = Object.values(props.website.integrations).find(
      (i) => i.integration_type == integrationType && i.status == true
    );
    const integrationService = new ReviewsIntegrationsService(integrationType, integrationObject, props.website.id, true);

    new Promise(async (resolve, reject) => {
      const allReviews = await integrationService.getStoreReviews();
      
      // In builder or section template preview, combine real and sample reviews to hit max count
      // Check builder/preview using loose equality to catch both true and "true"
      const isBuilder = props.builder == true;
      const isPreview = props.preview == true;
      const shouldAddSamples = isBuilder || isPreview;
      
      let finalReviews;
      
      if (shouldAddSamples) {
        const filterType = element.options["reviews-filter-type"] ? element.options["reviews-filter-type"] : "selection";
        const maxCount = element.options['reviews-max-count'];
        const targetCount = maxCount && parseInt(maxCount) > 0 ? parseInt(maxCount) : 6;
        
        // Get filtered reviews without max count limit by creating a copy with high limit
        // Use Object.assign to create a shallow copy, then copy options separately
        const elementCopy = Object.assign({}, element);
        elementCopy.options = Object.assign({}, element.options);
        elementCopy.options['reviews-max-count'] = '9999'; // Set very high to get all matching reviews
        
        // Get filtered reviews without max count limit
        let reviewsWithoutLimit = ReviewService.clientFilteredReviews(allReviews, elementCopy);
        
        // Deduplicate real reviews by ID to ensure each real review appears only once
        const seenIds = new Set();
        const uniqueRealReviews = reviewsWithoutLimit.filter(review => {
          const reviewId = review.id?.toString();
          if (seenIds.has(reviewId)) {
            return false; // Skip duplicate
          }
          seenIds.add(reviewId);
          return true; // Keep unique review
        });
        
        // Special case: if filter_type is "selection", only use sample reviews if there are no real reviews
        if (filterType === "selection") {
          if (uniqueRealReviews.length === 0) {
            // No real reviews: use sample reviews
            const sampleReviews = generateSampleReviews(3, uniqueRealReviews);
            finalReviews = [...uniqueRealReviews, ...sampleReviews];
          } else {
            // Has real reviews: use only real reviews (respect max count) - NO sample reviews
            finalReviews = uniqueRealReviews.slice(0, targetCount);
          }
        } else {
          // Otherwise, fill up to max count with sample reviews if needed
          if (uniqueRealReviews.length < targetCount) {
            const neededCount = targetCount - uniqueRealReviews.length;
            const sampleReviews = generateSampleReviews(neededCount, uniqueRealReviews);
            finalReviews = [...uniqueRealReviews, ...sampleReviews];
          } else {
            finalReviews = uniqueRealReviews;
          }
          
          // Ensure we don't exceed the max count
          finalReviews = finalReviews.slice(0, targetCount);
        }
      } else {
        // Normal flow: use existing filtering logic
        finalReviews = ReviewService.clientFilteredReviews(allReviews, element);
      }

      resolve(finalReviews);
    }).then((reviews) => {
      setReviewsList(reviews);
      setCurrentSlide(0);
    });
  }, [
    element.options["reviews-ids"],
    element.options["reviews-filter-type"],
    element.options['reviews-source'],
    element.options["reviews-sorting"],
    element.options["reviews-sorting-operator"],
    element.options["reviews-filters"],
    element.options["reviews-filters-keyword"],
    element.options["reviews-filters-rating-condition"],
    element.options["reviews-filters-rating-value"],
    element.options["reviews-filters-has-video"],
    element.options["reviews-filters-date-condition"],
    element.options["reviews-filters-date-value"],
    element.options["reviews-max-count"],
    element.options["reviews-last-updated"]
  ]);

  useEffect(() => {
    const reviewsPerColumn =
      contentFlow === 'collage'
        ? ReviewService.createCollage(reviewsList, reviewsPerRow)
        : { 0: reviewsList };

    setReviewsColumns(reviewsPerColumn);
  }, [contentFlow, reviewsList, props.device, reviewsPerRow, element.options["reviews-ids"]]);

  const totalSlides = Math.ceil(sortedReviews?.length / reviewsPerSlide);

  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  const reviewPlaceholder = () => {
    // Show placeholder when no reviews and we're in builder or section template preview
    if (sortedReviews?.length > 0 || (props.builder != true && props.preview != true))
      return null;

    return (
      <div className="reviews-fallback-empty-example opacity-50" >
        {props.device == "desktop" && (
          <img loading="lazy" src={ReviewsExampleDesktop} />
        )}

        {props.device == "mobile" && (
          <img loading="lazy" src={ReviewsExampleMobile} />
      )}
    </div>
    )
  }

  return (
    <React.Fragment>
      <div
        className={`element-content cf-reviews-element`}
        data-element-id={element.id}
      >
        {reviewPlaceholder()}

        <div className={`cf-reviews-container ${sliderEnabled ? `cf-reviews-slider-enabled cf-reviews-style-${sliderStyle}` : 'cf-reviews-slider-disabled'} cf-reviews-layout-${reviewsLayout}`}>
          {sliderEnabled ? (
            <>
              <div className="cf-reviews-slider-wrapper">
                <div className="cf-reviews-slider">
                  <div
                    className="cf-reviews-slides"
                    style={{
                      transform: `translateX(-${currentSlide * 100}%)`,
                      transition: 'transform 0.3s ease-in-out'
                    }}
                  >
                    {Array.from({ length: totalSlides }, (_, slideIndex) => (
                      <div key={slideIndex} className="cf-reviews-slide">
                        <div className="cf-reviews-slide-content">
                          {sortedReviews
                            .slice(slideIndex * reviewsPerSlide, (slideIndex + 1) * reviewsPerSlide)
                            .map((review) => (
                              <ReviewContentBox
                                key={`cf-reviews-content-box-${review.id}`}
                                review={review}
                                element={element}
                                builder={shouldRenderVideoPlaceholders}
                                variantOptions={variantOptions}
                              />
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {totalSlides > 1 && showArrows && sliderStyle === 'default' && (
                  <>
                    <button
                      className="cf-reviews-nav-arrow cf-reviews-prev cf-reviews-arrow-default"
                      onClick={goToPrevSlide}
                      aria-label="Previous reviews"
                    >
                      <svg width="1em" height="1em" viewBox="0 0 320 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M34.52 239.03l194.34-194.34c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.01 154.74c9.33 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"/>
                      </svg>
                    </button>

                    <button
                      className="cf-reviews-nav-arrow cf-reviews-next cf-reviews-arrow-default"
                      onClick={goToNextSlide}
                      aria-label="Next reviews"
                    >
                      <svg width="1em" height="1em" viewBox="0 0 320 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M285.476 272.971L91.136 467.311c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L201.491 256 47.471 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {totalSlides > 1 && (showIndicators || (showArrows && sliderStyle === 'inline')) && (
                <div className={`cf-reviews-navigation cf-reviews-navigation-bottom ${
                  sliderStyle === 'inline' && ((showIndicators && !showArrows) || (!showIndicators && showArrows))
                    ? 'cf-reviews-navigation-single'
                    : ''
                }`}>
                  {showIndicators && (
                    <div className="cf-reviews-pagination">
                      {Array.from({ length: totalSlides }, (_, index) => (
                        <button
                          key={index}
                          className={`cf-reviews-dot ${index === currentSlide ? 'active' : ''}`}
                          onClick={() => goToSlide(index)}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  {showArrows && sliderStyle === 'inline' && (
                    <div className="cf-reviews-arrows-inline">
                      <button
                        className="cf-reviews-nav-arrow cf-reviews-prev cf-reviews-arrow-inline"
                        onClick={goToPrevSlide}
                        aria-label="Previous reviews"
                      >
                        <svg width="1em" height="1em" viewBox="0 0 320 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M34.52 239.03l194.34-194.34c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.01 154.74c9.33 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"/>
                        </svg>
                      </button>

                      <button
                        className="cf-reviews-nav-arrow cf-reviews-next cf-reviews-arrow-inline"
                        onClick={goToNextSlide}
                        aria-label="Next reviews"
                      >
                        <svg width="1em" height="1em" viewBox="0 0 320 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M285.476 272.971L91.136 467.311c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L201.491 256 47.471 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            (() => {
              const columnsToRender =
                Object.keys(reviewsColumns).length > 0 ? reviewsColumns : { 0: sortedReviews };
              return Object.keys(columnsToRender).map((column) => (
                <div key={`cf-reviews-column-${column}`} className="cf-reviews-grid">
                  {columnsToRender[column].map((review) => (
                    <ReviewContentBox
                      key={`cf-reviews-content-box-${review.id}`}
                      review={review}
                      element={element}
                      builder={shouldRenderVideoPlaceholders}
                      variantOptions={variantOptions}
                    />
                  ))}
                </div>
              ));
            })()
          )}
        </div>

        <BuilderStyleTag
          builder={props.builder}
          styleKey={`cta:${props.cta.id}:element:${element.id}:reviews`}
          stylesheet={stylesheet}
        />
      </div>
    </React.Fragment>
  );
};

export default ElementReviews;
